import type { Dispatch, SetStateAction } from "react";
import type { TrainingAnswer } from "../../../lib/training/types";
import { adminPost } from "@/lib/kbAdminClient";

type MessageTone = { tone: "ok" | "err"; text: string } | null;

type TrainingAskProposalFormProps = {
  answerMode: "kb" | "contacts" | "location";
  proposeOpen: boolean;
  scopedOrganizationId: string;
  proposeTitle: string;
  setProposeTitle: Dispatch<SetStateAction<string>>;
  proposeBody: string;
  setProposeBody: Dispatch<SetStateAction<string>>;
  proposeSubmitting: boolean;
  setProposeSubmitting: Dispatch<SetStateAction<boolean>>;
  proposeMessage: MessageTone;
  setProposeMessage: Dispatch<SetStateAction<MessageTone>>;
  result: TrainingAnswer;
  resultQuery: string;
  lastQuery: string;
  query: string;
  salespersonName?: string;
  salespersonFirstName?: string;
};

export function TrainingAskProposalForm(props: TrainingAskProposalFormProps) {
  if (!(props.answerMode === "kb" && props.proposeOpen && props.scopedOrganizationId)) return null;

  return (
    <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
      <div className="text-xs font-semibold text-indigo-900">Propose a new org KB article (admin review)</div>
      <p className="text-xs text-gray-600">
        Your question, the AI answer, and sources are attached for reviewers. Write the article text you want
        published if approved.
      </p>
      <label className="block text-xs text-gray-700">
        Title
        <input className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm" value={props.proposeTitle} onChange={(e) => props.setProposeTitle(e.target.value)} maxLength={200} />
      </label>
      <label className="block text-xs text-gray-700">
        Proposed article (Markdown)
        <textarea className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm min-h-[140px] font-mono" value={props.proposeBody} onChange={(e) => props.setProposeBody(e.target.value)} placeholder="Correct or extend what should appear in the KB..." />
      </label>
      {props.proposeMessage ? (
        <div className={props.proposeMessage.tone === "ok" ? "text-xs text-emerald-800" : "text-xs text-red-700"}>
          {props.proposeMessage.text}
        </div>
      ) : null}
      <button
        type="button"
        disabled={props.proposeSubmitting || !props.proposeTitle.trim() || props.proposeBody.trim().length < 20}
        className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
        onClick={async () => {
          props.setProposeSubmitting(true);
          props.setProposeMessage(null);
          const editedBy = props.salespersonName?.trim() || (props.salespersonFirstName ? `${props.salespersonFirstName}` : "") || "trainer";
          const contextSources = props.result.hits.map((h) => ({
            id: h.id,
            title: h.title,
            score: h.score,
            url: h.url,
          }));
          const resp = await adminPost<{ ok: true; message?: string }>("/api/kb/admin/submitGeneralInfoProposal", {
            title: props.proposeTitle.trim(),
            proposedBodyMarkdown: props.proposeBody.trim(),
            contextQuery: props.resultQuery || props.lastQuery || props.query.trim() || undefined,
            contextAiAnswer: props.result.answer,
            contextSources,
            editedBy,
            doc_type: "training",
          });
          props.setProposeSubmitting(false);
          if (!resp.ok) {
            props.setProposeMessage({ tone: "err", text: resp.error });
            return;
          }
          props.setProposeMessage({
            tone: "ok",
            text: resp.data.message || "Submitted. An admin can approve it in KB -> Change proposals.",
          });
          props.setProposeBody("");
        }}
      >
        {props.proposeSubmitting ? "Submitting..." : "Submit for review"}
      </button>
    </div>
  );
}
